// /admin/billing — the operator's platform subscription (COL-352).
//
// Checkout is Stripe-hosted (create-operator-checkout → redirect) and
// supports BOTH rails — card and SEPA-Lastschrift (Ganesh, Aug 20). Stripe
// hosts the card entry (PCI-safe) and renders the SEPA mandate text itself;
// billing-webhook's checkout.session.completed handler creates the
// saas_subscriptions row, which this page polls after the return redirect
// (?status=success).
//
// States, driven by useOperatorEntitlement():
//   none/canceled → plan card with the hosted-checkout CTA
//   returned      → poll until 'active', then Neel's post-pay direction:
//                   "Create your first property" BEFORE tenants/owners
//   active        → read-only summary
//   past_due      → warning + summary (writes are re-blocked server-side)
//
// Non-operators are redirected out — CoLiviq staff and owners have no bill.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useOperator } from "@/hooks/useOperator";
import { useOperatorEntitlement } from "@/hooks/useOperatorEntitlement";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  CreditCard,
  Landmark,
  Loader2,
} from "lucide-react";

function euros(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

const HostedCheckoutCard = () => {
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = async () => {
    setError(null);
    setRedirecting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "create-operator-checkout",
        {
          body: {
            successUrl: `${window.location.origin}/admin/billing?status=success`,
            cancelUrl: `${window.location.origin}/admin/billing?status=canceled`,
          },
        }
      );
      if (fnError || data?.error) {
        throw new Error(data?.error ?? fnError?.message ?? "Checkout failed");
      }
      if (data?.alreadySubscribed) {
        // The webhook beat the UI — a reload shows the summary.
        window.location.assign("/admin/billing?status=success");
        return;
      }
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.assign(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setRedirecting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>coliviq SaaS Pro</CardTitle>
        <CardDescription>
          One monthly subscription for the full operations console — properties,
          tenants, contracts, rent tracking and support under your own name.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CreditCard className="h-4 w-4" aria-hidden />
            Card
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Landmark className="h-4 w-4" aria-hidden />
            SEPA-Lastschrift
          </span>
          <span>· cancel any time</span>
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button
          onClick={startCheckout}
          disabled={redirecting}
          className="w-full"
        >
          {redirecting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Opening secure checkout…
            </>
          ) : (
            "Subscribe — choose card or direct debit"
          )}
        </Button>
        <p className="text-xs text-muted-foreground">
          Payment details are entered on Stripe's secure page; nothing is stored
          on coliviq's servers.
        </p>
      </CardContent>
    </Card>
  );
};

const OperatorBillingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { rolesReady, isOperatorPortal } = useAuth();
  const { operator } = useOperator();
  const { status, subscription, isEntitled, isLoading, refetch } =
    useOperatorEntitlement();
  const returnedFromCheckout = searchParams.get("status") === "success";
  const [pollingDone, setPollingDone] = useState(false);
  const pollRef = useRef<number | null>(null);

  // Only operator-portal accounts have a bill.
  useEffect(() => {
    if (rolesReady && !isOperatorPortal)
      navigate("/admin?tab=analytics", { replace: true });
  }, [rolesReady, isOperatorPortal, navigate]);

  // Back from Stripe: poll until the checkout.session.completed webhook has
  // written the row and it reads 'active'.
  useEffect(() => {
    if (!returnedFromCheckout || isEntitled) return;
    pollRef.current = window.setInterval(() => refetch(), 2500);
    const stop = window.setTimeout(() => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      setPollingDone(true);
    }, 60_000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      window.clearTimeout(stop);
    };
  }, [returnedFromCheckout, isEntitled, refetch]);

  const heading = useMemo(
    () => (operator ? `${operator.name} — subscription` : "Subscription"),
    [operator]
  );

  if (!rolesReady || isLoading) {
    return (
      <AdminV2Layout title={heading}>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2
            className="h-6 w-6 animate-spin text-muted-foreground"
            aria-hidden
          />
        </div>
      </AdminV2Layout>
    );
  }

  return (
    <AdminV2Layout title={heading}>
      <div className="mx-auto max-w-2xl space-y-6">
        {isEntitled && returnedFromCheckout && (
          <Card className="border-primary/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" aria-hidden />
                You're in — let's set things up in the right order
              </CardTitle>
              <CardDescription>
                Start with a property: apartments, rooms, owners and tenants all
                hang off it.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button asChild>
                <Link to="/admin/properties/new">
                  <Building2 className="mr-2 h-4 w-4" aria-hidden />
                  Create your first property
                </Link>
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/admin?tab=analytics">Later — back to overview</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {returnedFromCheckout && !isEntitled && !pollingDone && (
          <Card>
            <CardContent className="flex items-center gap-3 py-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              Confirming your subscription with Stripe…
            </CardContent>
          </Card>
        )}

        {status === "past_due" && (
          <Card className="border-amber-500/50">
            <CardContent className="flex items-start gap-3 py-4 text-sm">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 text-amber-500"
                aria-hidden
              />
              <span>
                Your last payment failed, so creating and editing is paused. We
                retry automatically — make sure the account or card is covered,
                or contact support.
              </span>
            </CardContent>
          </Card>
        )}

        {subscription ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {subscription.plan}
                <Badge
                  variant={status === "active" ? "default" : "destructive"}
                >
                  {status}
                </Badge>
              </CardTitle>
              <CardDescription>
                {euros(subscription.amount_cents)} / month
                {subscription.started_at
                  ? ` · since ${subscription.started_at}`
                  : ""}
                {subscription.current_period_end
                  ? ` · renews ${new Date(
                      subscription.current_period_end
                    ).toLocaleDateString("de-DE")}`
                  : ""}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : !returnedFromCheckout || pollingDone ? (
          <HostedCheckoutCard />
        ) : null}
      </div>
    </AdminV2Layout>
  );
};

export default OperatorBillingPage;
